import React, { useEffect, useState } from 'react';
import { Row, Col, Typography, Avatar, Layout, Button, Modal, Form, Input, message, Tag } from 'antd';
import { 
  UserOutlined, 
  LockOutlined,
  MailOutlined,
  CalendarOutlined,
  EditOutlined,
  LogoutOutlined,
  PhoneOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import MainLayout from '../components/layout/MainLayout';
import { getUserProfile, UserProfile } from '../services/userService';
import { changePassword, logout } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title, Paragraph } = Typography;

interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// 添加屏幕尺寸判断函数
const getScreenSize = (width: number): string => {
  if (width <= 768) return 'mobile'; // 8英寸以下
  if (width <= 1024) return 'tablet'; // 10英寸
  if (width <= 1366) return 'laptop'; // 14英寸
  if (width <= 1920) return 'desktop'; // 17-19英寸
  return 'large'; // 19英寸以上
};

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [screenSize, setScreenSize] = useState(getScreenSize(window.innerWidth));
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [form] = Form.useForm<PasswordFormValues>();
  const [profileForm] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);

  // 修改响应式处理
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize(getScreenSize(width));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileData = await getUserProfile();
        setProfile(profileData);
        profileForm.setFieldsValue(profileData);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [profileForm]);

  const handlePasswordChange = async (values: PasswordFormValues) => {
    try {
      setSubmitLoading(true);
      await changePassword(values.oldPassword, values.newPassword);
      message.success('密码修改成功');
      setIsPasswordModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('密码修改失败，请重试');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <MainLayout>
      <Layout.Content style={{ 
        padding: 0,
        background: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%'
      }}>
        {/* Banner 区域 */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1890ff 0%, #52c41a 100%)',
          padding: '24px',
          color: '#fff',
          textAlign: 'center',
          width: '100%',
          boxSizing: 'border-box',
          marginBottom: '24px',
          borderRadius: '8px'
        }}>
          <Title level={2} style={{ color: '#fff', margin: 0 }}>
            个人中心
          </Title>
          <Paragraph style={{ color: 'rgba(255, 255, 255, 0.85)', margin: '8px 0 0 0', fontSize: '16px' }}>
            查看你的训练进度和历史记录
          </Paragraph>
        </div>

        {/* 间隔区域 */}
        {/* <div style={{ height: '24px', background: '#f5f5f5' }} /> */}

        {/* 主要内容区域 */}
        <div style={{ 
          padding: 0,
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          width: '100%',
          boxSizing: 'border-box',
          gap: '24px'
        }}>
          {/* 个人信息区域 */}
          <div style={{ 
            background: '#fff',
            borderRadius: '8px',
            padding: screenSize === 'mobile' ? '12px' : '24px'
          }}>
            <Row gutter={[16, 16]} style={{ margin: 0 }}>
              {/* 左侧个人信息 */}
              <Col span={10} style={{ padding: 0 }}>
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px'
                }}>
                  {/* 标题 */}
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: '#1f1f1f',
                      marginBottom: '12px'
                    }}>
                      <UserOutlined style={{ 
                        color: '#ff4d4f',
                        fontSize: '16px'
                      }} />
                      <span style={{ 
                        fontSize: '15px', 
                        fontWeight: 500 
                      }}>个人信息</span>
                    </div>
                    <div style={{
                      height: '1px',
                      background: 'linear-gradient(to right, transparent, #e8e8e8 20%, #e8e8e8 80%, transparent)',
                      margin: '0 0 ' + (screenSize === 'mobile' ? '12px' : '24px') + ' 0'
                    }} />
                  </div>

                  {/* 个人信息区域 */}
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: screenSize === 'mobile' ? '12px' : '24px',
                    justifyContent: 'center',
                    flexDirection: screenSize === 'mobile' ? 'column' : 'row',
                    marginTop: screenSize === 'mobile' ? '-8px' : 0
                  }}>
                    {/* 头像 */}
                    <Avatar 
                      size={screenSize === 'mobile' ? 56 : 80}
                      icon={<UserOutlined style={{ 
                        fontSize: screenSize === 'mobile' ? '28px' : '40px' 
                      }} />} 
                      style={{
                        backgroundColor: '#ff4d4f',
                        boxShadow: '0 4px 12px rgba(255, 77, 79, 0.2)',
                        flexShrink: 0,
                        marginBottom: screenSize === 'mobile' ? '8px' : 0
                      }}
                    />
                    
                    {/* 用户信息 */}
                    <div style={{ flex: 'none' }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: screenSize === 'mobile' ? '8px' : '12px',
                        alignItems: screenSize === 'mobile' ? 'center' : 'flex-start'
                      }}>
                        {/* 用户名区域 */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          width: '100%'
                        }}>
                          <UserOutlined style={{ 
                            color: '#8c8c8c',
                            fontSize: screenSize === 'mobile' ? '16px' : '18px'
                          }} />
                          <div style={{ 
                            fontSize: screenSize === 'mobile' ? '15px' : '16px', 
                            fontWeight: 500,
                            color: '#262626'
                          }}>
                            {profile?.username}
                          </div>
                        </div>

                        {/* 邮箱 */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          width: '100%'
                        }}>
                          <MailOutlined style={{ 
                            color: '#8c8c8c',
                            fontSize: screenSize === 'mobile' ? '16px' : '18px'
                          }} />
                          <div style={{ 
                            fontSize: screenSize === 'mobile' ? '15px' : '16px',
                            color: '#262626',
                            wordBreak: 'break-all'
                          }}>
                            {profile?.email}
                          </div>
                        </div>

                        {/* 手机号码 */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          width: '100%'
                        }}>
                          <PhoneOutlined style={{ 
                            color: '#8c8c8c',
                            fontSize: screenSize === 'mobile' ? '16px' : '18px'
                          }} />
                          <div style={{ 
                            fontSize: screenSize === 'mobile' ? '15px' : '16px',
                            color: '#262626',
                            wordBreak: 'break-all'
                          }}>
                            {profile?.phone}
                          </div>
                        </div>

                        {/* 注册时间 */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          width: '100%'
                        }}>
                          <CalendarOutlined style={{ 
                            color: '#8c8c8c',
                            fontSize: screenSize === 'mobile' ? '16px' : '18px'
                          }} />
                          <div style={{ 
                            fontSize: screenSize === 'mobile' ? '15px' : '16px',
                            color: '#262626'
                          }}>
                            {profile?.created_at ? dayjs(profile.created_at).format('YYYY-MM-DD HH:mm:ss') : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 操作按钮组 */}
                  <Row justify="center" gutter={[16, 16]} style={{ 
                    marginTop: screenSize === 'mobile' ? '8px' : '16px',
                    marginBottom: screenSize === 'mobile' ? '4px' : '8px'
                  }}>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Button 
                        type="default"
                        icon={<EditOutlined />}
                        block
                        onClick={() => setIsPasswordModalVisible(true)}
                        style={{
                          borderColor: '#d9d9d9',
                          color: '#595959',
                          height: screenSize === 'mobile' ? '32px' : '36px'
                        }}
                      >
                        修改密码
                      </Button>
                    </Col>
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <Button 
                        type="default"
                        icon={<LogoutOutlined />}
                        block
                        onClick={() => {
                          Modal.confirm({
                            title: '确认退出',
                            content: '确定要退出系统吗？',
                            okText: '确认',
                            cancelText: '取消',
                            onOk: () => {
                              logout();
                              navigate('/login');
                            }
                          });
                        }}
                        style={{
                          borderColor: '#ff7875',
                          color: '#ff4d4f',
                          height: screenSize === 'mobile' ? '32px' : '36px'
                        }}
                      >
                        退出系统
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Col>

              {/* 分割线 */}
              <Col span={1} style={{ 
                padding: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'stretch'
              }}>
                <div style={{
                  width: '1px',
                  background: 'linear-gradient(to bottom, transparent, #e8e8e8 20%, #e8e8e8 80%, transparent)',
                  margin: '0 auto'
                }} />
              </Col>

              {/* 右侧统计数据 */}
              <Col span={13} style={{ padding: 0 }}>
                <div>
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      color: '#1f1f1f',
                      marginBottom: '12px'
                    }}>
                      <UserOutlined style={{ 
                        color: '#ff4d4f',
                        fontSize: '16px'
                      }} />
                      <span style={{ 
                        fontSize: '15px', 
                        fontWeight: 500 
                      }}>个人资料</span>
                    </div>
                    <div style={{
                      height: '1px',
                      background: 'linear-gradient(to right, transparent, #e8e8e8 20%, #e8e8e8 80%, transparent)',
                      margin: '0 0 24px 0'
                    }} />
                  </div>

                  <Form 
                    form={profileForm}
                    layout="vertical"
                  >
                    <Form.Item
                      label="用户名"
                      name="username"
                    >
                      <Input 
                        placeholder="用户名" 
                        readOnly 
                        style={{
                          backgroundColor: '#f5f5f5',
                          cursor: 'not-allowed'
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="邮箱"
                      name="email"
                    >
                      <Input 
                        placeholder="邮箱地址" 
                        readOnly 
                        style={{
                          backgroundColor: '#f5f5f5',
                          cursor: 'not-allowed'
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="手机号码"
                      name="phone"
                    >
                      <Input 
                        placeholder="手机号码" 
                        readOnly 
                        style={{
                          backgroundColor: '#f5f5f5',
                          cursor: 'not-allowed'
                        }}
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button type="primary" style={{
                        background: 'linear-gradient(120deg, #1890ff 0%, #52c41a 100%)',
                        border: 'none',
                        boxShadow: '0 2px 6px rgba(24, 144, 255, 0.2)'
                      }}>
                        保存修改
                      </Button>
                    </Form.Item>
                  </Form>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </Layout.Content>

      {/* 修改密码模态框 */}
      <Modal
          title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: '#1f1f1f'
          }}>
              <LockOutlined style={{ color: '#ff4d4f' }} />
              <span>修改密码</span>
            </div>
          }
        open={isPasswordModalVisible}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
          onFinish={handlePasswordChange}
          style={{ marginTop: '24px' }}
          >
            <Form.Item
              name="oldPassword"
              label="当前密码"
              rules={[
                { required: true, message: '请输入当前密码' },
                { min: 6, message: '密码至少6个字符' }
              ]}
            >
            <Input.Password 
              placeholder="请输入当前密码"
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
            />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码至少6个字符' }
              ]}
            >
            <Input.Password 
              placeholder="请输入新密码"
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
            />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认新密码"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
            <Input.Password 
              placeholder="请再次输入新密码"
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
            />
            </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button 
              type="default" 
              style={{ marginRight: '12px' }}
              onClick={() => {
                setIsPasswordModalVisible(false);
                form.resetFields();
              }}
            >
              取消
            </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitLoading}
                style={{
                  background: 'linear-gradient(120deg, #ff4d4f 0%, #ff7875 100%)',
                  border: 'none',
                  boxShadow: '0 2px 6px rgba(255, 77, 79, 0.2)'
                }}
              >
                确认修改
              </Button>
            </Form.Item>
          </Form>
      </Modal>
    </MainLayout>
  );
};

// Update styles
const style = document.createElement('style');
style.innerHTML = `
  .profile-menu.ant-menu {
    color: rgba(255, 255, 255, 0.9);
  }
  .profile-menu.ant-menu-inline {
    border-right: none;
  }
  .profile-menu.ant-menu-inline.ant-menu-inline-collapsed {
    width: auto;
  }
  .profile-menu.ant-menu-inline.ant-menu-inline-collapsed .ant-menu-item {
    padding: 12px !important;
    text-align: center;
    margin: 8px;
    border-radius: 6px;
  }
  .profile-menu.ant-menu-vertical .ant-menu-item,
  .profile-menu.ant-menu-inline .ant-menu-item {
    margin: 4px 8px;
    padding: 12px 16px !important;
    color: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease;
    line-height: 1.5;
    height: auto;
    border-radius: 6px;
  }
  .profile-menu .ant-menu-item:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    backdrop-filter: blur(4px);
  }
  .profile-menu .ant-menu-item-selected {
    background: rgba(255, 255, 255, 0.2) !important;
    color: #fff !important;
    backdrop-filter: blur(4px);
  }
  .profile-menu .ant-menu-item::after {
    display: none;
  }
  .profile-menu .anticon {
    color: rgba(255, 255, 255, 0.9);
    margin-right: 12px;
    font-size: 18px;
  }
  .profile-menu.ant-menu-inline-collapsed .anticon {
    margin: 0;
    font-size: 20px;
  }
  .profile-menu .ant-menu-item:hover .anticon,
  .profile-menu .ant-menu-item-selected .anticon {
    color: #fff;
  }
  .collapse-button:hover {
    color: #fff !important;
    background: rgba(255, 255, 255, 0.1) !important;
  }
`;
document.head.appendChild(style);

export default Profile;